// @ts-check
import classNames from 'classnames';
import { graphql } from 'gatsby';
import React from 'react';
import { ConstructFragmentOverride, GetCopyFromJson } from '~/contentstack';
import LinkButton from '../components/LinkButton';

export const query = graphql`
	fragment PageHeroLeftTextFragment on Ozk_page_heroCover_content {
		left_text {
			bottom_button {
				button_link
				button_style
				button_text
				open_in_new_tab
			}
			copy_jrte {
				uid
				_version
				json
			}
			heading_jrte {
				uid
				_version
				json
			}
			text_theme
			min_height
		}
	}
`;

/**
 * @typedef PageHeroLeftTextOptions
 * @property {string} uid // This is a fake field.
 * @property {import('../components/LinkButton').ButtonOption[]} [bottom_button]
 * @property {import('../CSTypes').CSJRTE} copy_jrte
 * @property {import('../CSTypes').CSJRTE} heading_jrte
 * @property {'light' | 'dark'} text_theme
 * @property {number} [min_height] An optional override to the block's min height.
 */
/**
 * @typedef PageHeroLeftTextProps
 * @property {import('~/contentstack/types').ModelWithLivePreviewAttr<PageHeroLeftTextOptions>} [options]
 */
/** @type {import('~/types/React').ReactFC<PageHeroLeftTextProps>} */
const PageHeroLeftText = ({ options }) => {
	if (!options) {
		return null;
	}

	const pFrag = ConstructFragmentOverride('p');

	const hasButton =
		!!options.bottom_button && Array.isArray(options.bottom_button)
			? options.bottom_button.length > 0
			: !!options.bottom_button;

	// Absolute minimum height is 8rem to hold the 3.375rem H1 with spacing.
	const parsedHeight = !options.min_height ? undefined : `max(${options.min_height}px, 8rem)`;

	return (
		<div
			className={classNames('page-hero-left-text', {
				'text-theme-dark': options.text_theme === 'dark',
				'text-theme-light': options.text_theme === 'light',
				'manual-height': !!options.min_height,
			})}
			style={{ minHeight: parsedHeight }}
		>
			{/* Add jrte heading */}
			{!!options.heading_jrte && (
				<h1 className="heading" {...options.heading_jrte.$?.uid}>
					{GetCopyFromJson(options.heading_jrte, pFrag)}
				</h1>
			)}

			{/* Add in optional jrte copy */}
			{!!options.copy_jrte && (
				<p className="copy" {...options.copy_jrte?.$?.uid}>
					{GetCopyFromJson(options.copy_jrte, pFrag)}
				</p>
			)}

			{hasButton && (
				<div className="button-container">
					<LinkButton className="bottom-button" options={options.bottom_button?.[0]} />
				</div>
			)}
		</div>
	);
};

export default PageHeroLeftText;
